// Binary downloads: fetch with the session cookie, read the server file name from Content-Disposition and save the blob.
import { apiFetch } from "@/api/client";
import { ApiProblem } from "@/api/problem";

export function fileNameFromDisposition(header: string | null, fallback: string): string {
  if (!header) {
    return fallback;
  }
  const encoded = /filename\*=UTF-8''([^;]+)/iu.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return fallback;
    }
  }
  const quoted = /filename="([^"]+)"/iu.exec(header) ?? /filename=([^;]+)/iu.exec(header);
  return quoted?.[1]?.trim() ?? fallback;
}

export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

export async function fetchFile(
  path: string,
  fallbackName: string,
  init?: RequestInit,
): Promise<DownloadedFile> {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    throw await ApiProblem.fromResponse(response);
  }
  const blob = await response.blob();
  return {
    blob,
    fileName: fileNameFromDisposition(response.headers.get("Content-Disposition"), fallbackName),
  };
}

export async function downloadFile(path: string, fallbackName: string, init?: RequestInit): Promise<string> {
  const file = await fetchFile(path, fallbackName, init);
  saveBlob(file.blob, file.fileName);
  return file.fileName;
}
